/**
 * User Profile API — 用户资料 & preferences
 */
import { ApiClient, API_BASE_URL } from './client';
import type { ApiResponse } from './types';
import { authFetch } from '@/lib/auth/session';
import { translateClient } from '@/lib/i18n/client';
import type { SkillVideoPreference } from '@/types/chat';
import { ImageUploadSafetyError, inferContentType, prepareImageUploadFile } from './materials';
import { useAuthStore } from '@/stores/authStore';
import { getAuthScopeRevision, registerAuthScopedCacheReset } from '@/lib/auth-scoped-state-reset';
import { createScopedReadCache } from './scoped-read-cache';

const AVATAR_PREPARE_MAX_SOURCE_BYTES = 64 * 1024 * 1024;
// Share only in-flight reads. Profile mutations must always observe fresh data.
const profileReads = createScopedReadCache<ApiResponse<UserProfile>>(0);
const PROFILE_READ_TIMEOUT_MS = 12_000;
registerAuthScopedCacheReset('user-profile-reads', () => profileReads.clear());

function avatarPreparationFailure(code: string): ApiResponse<File> {
  const key = code === 'IMAGE_SOURCE_TOO_LARGE' ? 'avatarSourceTooLarge'
    : code === 'INVALID_IMAGE' ? 'avatarSourceInvalid' : 'avatarPrepareFailed';
  return { success: false, error: { code, message: translateClient('nav', key) } };
}

export interface UserProfile {
  user_id: string;
  email?: string;
  display_name?: string;
  avatar_url?: string | null;
  bio?: string;
  tier?: string;
  created_at?: string;
  preferences: Record<string, unknown>;
}

export interface UpdateUserProfilePayload {
  display_name?: string | null;
  bio?: string | null;
  avatar_url?: string | null;
  preferences?: Record<string, unknown>;
}

export interface CompleteOnboardingPayload {
  version: 2;
  role: 'designer' | 'content_operations' | 'ecommerce' | 'marketing' | 'creator' | 'entrepreneur' | 'student' | 'employee' | 'freelancer' | 'ai_design_interest' | 'production_studio' | 'other';
  role_other?: string | null;
  ai_familiarity?: 'never' | 'occasional' | 'frequent';
  primary_goal: 'product_promotion' | 'fashion_promotion' | 'design' | 'ugc';
  product_category: 'fashion' | 'beauty' | 'ecommerce_retail' | 'electronics' | 'food' | 'home' | 'jewelry_accessories' | 'sports_outdoor' | 'mother_baby' | 'pets' | 'health_wellness' | 'other';
  product_category_other?: string | null;
  monthly_investment?: 'self_service' | 'under_500' | 'between_500_2000' | 'between_2000_5000' | 'over_5000';
  discovery_source: 'friend' | 'social_ads' | 'tiktok' | 'x' | 'reddit' | 'instagram' | 'redbook' | 'search' | 'other';
  discovery_source_other?: string | null;
  timezone?: string | null;
}

export interface SkipOnboardingPayload {
  version: 2;
  timezone?: string | null;
}

export type OnboardingCreationModule = 'product' | 'people' | 'design' | 'ugc';

export interface UpdateOnboardingActivationPayload {
  status: 'started' | 'completed' | 'dismissed';
  module?: OnboardingCreationModule;
}

export interface SkillVideoPreferenceUpdateResponse {
  skill_id: string;
  preference: Record<string, unknown>;
  preferences: Record<string, unknown>;
}

function serializeSkillVideoPreference(
  preference: SkillVideoPreference,
): Record<string, unknown> {
  return {
    provider: preference.provider,
    model: preference.model,
    duration_seconds: preference.duration,
    resolution: preference.resolution,
    aspect_ratio: preference.aspectRatio,
    generate_audio: preference.generateAudio,
    narration_enabled: preference.narrationEnabled,
    narration_language: preference.narrationLanguage,
    storyboard_enabled: preference.storyboardEnabled,
  };
}

function normalizeProfile(payload: UserProfile | { data: UserProfile }): UserProfile {
  const profile = ('data' in payload && payload.data ? payload.data : payload) as UserProfile;
  return {
    ...profile,
    preferences: profile.preferences || {},
  };
}

class UserApi extends ApiClient {
  /** Prepare a crop source without publishing or saving it as an avatar. */
  async prepareAvatarSource(file: File, signal: AbortSignal): Promise<ApiResponse<File>> {
    try {
      signal.throwIfAborted();
      if (file.size > AVATAR_PREPARE_MAX_SOURCE_BYTES) {
        return avatarPreparationFailure('IMAGE_SOURCE_TOO_LARGE');
      }
      const prepared = await prepareImageUploadFile(file, signal);
      signal.throwIfAborted();
      if (!['image/heic', 'image/heif'].includes(inferContentType(prepared))) {
        return { success: true, data: prepared };
      }
      const form = new FormData();
      form.append('file', prepared);
      const response = await authFetch(`${API_BASE_URL}/users/me/avatar/prepare`, {
        method: 'POST', body: form, signal,
      });
      signal.throwIfAborted();
      if (response.status === 401) {
        return { success: false, error: { code: 'UNAUTHORIZED',
          message: translateClient('errors', 'codes.unauthorized.message'), action: 'login' } };
      }
      if (!response.ok) {
        const envelope = await response.json().catch(() => ({}));
        signal.throwIfAborted();
        return avatarPreparationFailure(envelope.success === false
          && ['INVALID_IMAGE', 'IMAGE_SOURCE_TOO_LARGE'].includes(envelope.error)
          ? envelope.error : 'IMAGE_PREPARE_FAILED');
      }
      if (response.headers.get('content-type')?.split(';')[0].trim() !== 'image/jpeg') {
        return avatarPreparationFailure('IMAGE_PREPARE_FAILED');
      }
      const blob = await response.blob();
      signal.throwIfAborted();
      if (!blob.size || blob.size > 10 * 1024 * 1024) {
        return avatarPreparationFailure('IMAGE_PREPARE_FAILED');
      }
      const name = `${prepared.name.replace(/\.[^.]+$/, '') || 'avatar'}.jpg`;
      return { success: true, data: new File([blob], name, {
        type: 'image/jpeg', lastModified: prepared.lastModified,
      }) };
    } catch (error) {
      if (signal.aborted) throw error;
      return avatarPreparationFailure(error instanceof ImageUploadSafetyError
        ? 'IMAGE_SOURCE_TOO_LARGE' : 'IMAGE_PREPARE_FAILED');
    }
  }

  /** 获取当前用户资料（含 preferences） */
  async getProfile(): Promise<ApiResponse<UserProfile>> {
    const scope = JSON.stringify([useAuthStore.getState().user?.id ?? null, getAuthScopeRevision()]);
    return profileReads.read(scope, async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), PROFILE_READ_TIMEOUT_MS);
      try {
        let response = await this.request<UserProfile | { data: UserProfile }>(
          '/users/me/profile', { signal: controller.signal },
        );
        // Only replay this safe read on transient transport/gateway failures.
        // 503 already has a bounded retry in ApiClient; auth rejection never retries here.
        if (!controller.signal.aborted && ['NETWORK_ERROR', 'HTTP_500', 'HTTP_502', 'HTTP_504'].includes(response.error?.code ?? '')) {
          response = await this.request<UserProfile | { data: UserProfile }>(
            '/users/me/profile', { signal: controller.signal },
          );
        }
        if (response.success === false || response.error || !response.data) return response as ApiResponse<UserProfile>;
        return { success: true, data: normalizeProfile(response.data) };
      } finally {
        clearTimeout(timeout);
      }
    });
  }

  async updateProfile(payload: UpdateUserProfilePayload): Promise<ApiResponse<UserProfile>> {
    const response = await this.request('/users/me/profile', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    if (response.error) return response as ApiResponse<UserProfile>;
    profileReads.clear();
    return this.getProfile();
  }

  /** 浅合并更新 preferences（不覆盖未传入的 key） */
  async updatePreferences(
    prefs: Record<string, unknown>,
  ): Promise<ApiResponse<unknown>> {
    return this.updateProfile({ preferences: prefs });
  }

  async completeOnboarding(
    payload: CompleteOnboardingPayload,
  ): Promise<ApiResponse<UserProfile>> {
    return this.request<UserProfile>('/users/me/onboarding', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async skipOnboarding(
    payload: SkipOnboardingPayload,
  ): Promise<ApiResponse<UserProfile>> {
    return this.request<UserProfile>('/users/me/onboarding/skip', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateOnboardingActivation(
    payload: UpdateOnboardingActivationPayload,
  ): Promise<ApiResponse<UserProfile>> {
    return this.request<UserProfile>('/users/me/onboarding/activation', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /** 原子更新账号级的单个 Skill 配置，不发送其他 Skill 的旧快照。 */
  async updateSkillVideoPreference(
    skillId: string,
    preference: SkillVideoPreference,
  ): Promise<ApiResponse<SkillVideoPreferenceUpdateResponse>> {
    return this.request<SkillVideoPreferenceUpdateResponse>(
      `/users/me/preferences/skill-video-config/${encodeURIComponent(skillId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(serializeSkillVideoPreference(preference)),
      },
    );
  }

  async uploadAvatar(file: File): Promise<ApiResponse<{ url: string; message: string }>> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await authFetch(`${API_BASE_URL}/users/me/avatar`, {
        method: 'POST',
        body: formData,
      });

      if (response.status === 401) {
        return {
          error: {
            code: 'UNAUTHORIZED',
            message: translateClient('errors', 'codes.unauthorized.message'),
            action: 'login',
          },
        };
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          error: {
            code: `HTTP_${response.status}`,
            message: translateClient('nav', 'avatarUploadFailed'),
            details: data,
          },
        };
      }

      return { data };
    } catch (error) {
      return {
        error: {
          code: 'NETWORK_ERROR',
          message: translateClient('errors', 'codes.network.message'),
          action: 'retry',
          retryable: true,
          details: error instanceof Error ? { rawMessage: error.message } : undefined,
        },
      };
    }
  }
}

export const userApi = new UserApi();
